@if (loading) {
<div class="row">
  <mat-progress-bar mode="indeterminate" class="custom-loader"></mat-progress-bar>
</div>
}

<div class="container-fluid mb-5">

  <div class="dashboard-card shadow-sm border-primary">

    <div class="card-header d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <mat-icon class="header-icon">{{ getIconByType() }}</mat-icon>
        <div>
          <h5 class="mb-0 font-weight-bold text-primary-purple">{{ question?.question }}</h5>
          @if (question?.typology) {
          <small class="text-muted">
            {{ question?.typology | titlecase }} Analysis
          </small>
          }
        </div>
      </div>

      <div class="d-flex align-items-center">
        @if (currentChartType === 'line') {
        <mat-button-toggle-group [value]="isSmooth" (change)="setSmooth($event.value)"
          [hideMultipleSelectionIndicator]="true" [hideSingleSelectionIndicator]="true"
          class="custom-purple-toggle mr-3 shadow-sm">
          <mat-button-toggle [value]="true" matTooltip="Smooth Lines">
            <mat-icon>gesture</mat-icon>
          </mat-button-toggle>
          <mat-button-toggle [value]="false" matTooltip="Straight Lines">
            <mat-icon>linear_scale</mat-icon>
          </mat-button-toggle>
        </mat-button-toggle-group>
        }
        @if (availableChartTypes.length > 1) {
        <mat-button-toggle-group name="chartType" [value]="currentChartType" (change)="setChartType($event.value)"
          [hideMultipleSelectionIndicator]="true" [hideSingleSelectionIndicator]="true"
          class="custom-purple-toggle shadow-sm">
          <mat-button-toggle value="line" matTooltip="Line Chart">
            <mat-icon>show_chart</mat-icon>
          </mat-button-toggle>
          <mat-button-toggle value="bar" matTooltip="Bar Chart">
            <mat-icon>bar_chart</mat-icon>
          </mat-button-toggle>
        </mat-button-toggle-group>
        }
      </div>
    </div>

    <div class="card-body">

      @if (timelineIsDisabled) {
      <div class="container my-3">
        <p class="text-center text-muted">Timeline view disabled for this question type.</p>
      </div>
      } @else {
      <div class="chart-section mt-2">
        @if (question?.description) {
        <div class="alert alert-custom alert-custom-info mb-3 d-flex align-items-start" role="alert">
          <mat-icon class="mr-2 mt-1">info</mat-icon>
          <div>
            <strong class="text-dark">{{ 'generic.description' | translate }}:</strong> {{ question.description }}
          </div>
        </div>
        }

        @if (surveyService.isOpenedQuestion(question?.typology)) {
        <div class="alert alert-custom alert-custom-sentiment mb-3 d-flex align-items-start" role="alert">
          <mat-icon class="mr-2 mt-1">analytics</mat-icon>
          <div [innerHTML]="'components.surveys.question-history-dashboard.sentiment-score-description' | translate">
          </div>
        </div>
        <div class="chart-container rounded p-1">
          <div echarts [options]="chartOptions" class="demo-chart" style="height: 400px"></div>
        </div>
        }
        @if (!surveyService.isOpenedQuestion(question?.typology) && !surveyService.isGridQuestion(question?.typology)) {
        <div class="chart-container rounded p-1">
          <div echarts [options]="chartOptions" class="demo-chart" style="height: 400px"></div>
        </div>
        }
      </div>
      }

      @if (surveyService.isGridQuestion(question?.typology) || surveyService.isOpenedQuestion(question?.typology)) {
      <div class="mt-5 pt-4 border-top">
        <h6 class="text-uppercase text-muted letter-spacing-2 mb-3">History Flow</h6>

        @if (surveyService.isOpenedQuestion(question?.typology) && filteredContent) {
        <div class="mb-4">
          <div class="chart-container text-center p-3 bg-light position-relative" style="min-height: 250px;">
            @if (wordcloudLoading) {
            <div class="wordcloud-loader d-flex align-items-center justify-content-center position-absolute w-100 h-100"
              style="top: 0; left: 0; z-index: 10; background: rgba(255,255,255,0.7);">
              <mat-spinner diameter="40"></mat-spinner>
            </div>
            }
            <img class="img-fluid" style="max-height: 250px; width: 100%; object-fit: contain;"
              [src]="'https://quickchart.io/wordcloud?height=300&width=800&removeStopwords=true&fontScale=110&text=' + filteredContent"
              (load)="onWordcloudLoad()" />
          </div>
        </div>
        }

        <div #mermaid class="mermaid-container d-flex justify-content-center mb-4"></div>

        @if (surveyService.isGridQuestion(question?.typology)) {
        <div class="chart-container rounded p-1">
          <div echarts [options]="chartOptions" class="demo-chart" style="height: 500px"></div>
        </div>
        }
      </div>
      }

      <!-- Evolution Timeline -->
      @if (surveys && surveys.length > 0) {
      <div class="mt-5 pt-4 border-top">
        <h6 class="text-uppercase text-muted letter-spacing-2 mb-3">Question Evolution</h6>
        <div class="history-timeline">
          @for (s of surveys; track s.id) {
          <div class="timeline-item">
            <div class="timeline-dot" [class.dot-current]="s.id === currentSurvey?.id"></div>
            <div class="timeline-card">
              <div class="timeline-header">
                <h6>Session {{ s.version_number }}</h6>
                @if (s.survey_question?.final_decision) {
                <span [class]="getDecisionBadgeClass(s.survey_question.final_decision)">
                  {{ getDecisionLabel(s.survey_question.final_decision) }}
                </span>
                } @else {
                <span class="badge badge-light text-muted">Initial Draft</span>
                }
              </div>

              @if (s.survey_question?.customs?.changes && objectKeys(s.survey_question.customs.changes).length > 0) {
              <div class="changes-panel mt-3">
                <div class="changes-title">
                  <mat-icon>history_edu</mat-icon> Tracked Changes
                </div>

                @for (key of objectKeys(s.survey_question.customs.changes); track key) {
                <div class="change-category">
                  <div class="category-name">{{ formatChangeCategory(key) }}</div>
                  <div class="change-badges">
                    @if (key === 'general' || key === 'customs') {
                    @for (field of s.survey_question.customs.changes[key]; track field) {
                    <span class="change-badge">{{ field }}</span>
                    }
                    } @else if (key === 'attachments') {
                    <span class="change-badge">Count updated</span>
                    } @else if (key === 'choices' || key === 'grid') {
                    <span class="change-badge">Structure modified</span>
                    }
                  </div>
                </div>
                }
              </div>
              }
            </div>
          </div>
          }
        </div>
      </div>
      }

    </div>
  </div>
</div>